import React, { useState, useCallback, useMemo , useEffect} from "react";
import {
  VStack,
  Text,
  Progress,
  Box,
  useToast,
  Heading,
  HStack,
  Icon
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { MdCloudUpload, MdCheckCircle, MdError } from "react-icons/md";
import { FaFileAlt } from "react-icons/fa";

export const defaultAllowedTypes = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
};

const MultiFileUpload = ({
  onUploadComplete,
  onFileSelect,
  uploadToVapor,
  title = "Subir archivos",
  isUploading: isGlobalUploading, // Estado de carga global del padre
  allowedFileTypes = defaultAllowedTypes,
}) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [localUploading, setLocalUploading] = useState(false);
  const toast = useToast();

  useEffect(() => {
  if (!isGlobalUploading && !localUploading) {
    setUploadProgress({});
  }
}, [isGlobalUploading, localUploading]);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      // Manejo de archivos rechazados (tipo incorrecto)
      if (rejectedFiles.length > 0) {
        toast({
          title: "Archivo(s) no válido(s)",
          description: "Algunos archivos fueron ignorados porque no cumplen con el formato permitido.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      // Proceso de Carga a S3
      if (acceptedFiles.length > 0) {
        setLocalUploading(true);

        // Inicializar progreso visual
        const initialProgress = {};
        acceptedFiles.forEach(f => initialProgress[f.name] = 0);
        setUploadProgress(prev => ({ ...prev, ...initialProgress }));

        const uploadPromises = acceptedFiles.map(async (file) => {
          try {
            const vaporResponse = await uploadToVapor(file, (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: Math.round(progress * 100),
              }));
            });

            return {
              name: file.name,
              url: vaporResponse.url,
              vaporId: vaporResponse.uuid, // ID crítico para el backend
              bucket: vaporResponse.bucket,
              key: vaporResponse.key,
              headers: vaporResponse.headers,
              extension: file.name.split(".").pop().toLowerCase(),
            };
          } catch (error) {
            console.error(`Error uploading file ${file.name}:`, error);
            toast({
              title: "Error de carga",
              description: `No se pudo cargar el archivo ${file.name}.`,
              status: "error",
            });
            return null;
          }
        });

        const uploadedFiles = await Promise.all(uploadPromises);
        const successfulUploads = uploadedFiles.filter((file) => file !== null);

        setLocalUploading(false);

        // Notificar al padre (EnhancedFileUploadManager)
        if (successfulUploads.length > 0) {
          // onFileSelect abre el modal en el componente padre
          onFileSelect(successfulUploads);

          // Opcional: si el padre usa onUploadComplete para algo más
          if (onUploadComplete) onUploadComplete(successfulUploads);
        } else if (acceptedFiles.length > 0) {
           toast({
              title: "Fallo total",
              description: "Ningún archivo pudo cargarse al servidor.",
              status: "error",
            });
        }
      }
    },
    [uploadToVapor, onFileSelect, onUploadComplete, toast]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: allowedFileTypes,
    multiple: true,
    disabled: isGlobalUploading || localUploading, // Bloquear si ya se está procesando
  });

  const fileTypesText = useMemo(() => {
    const extensions = Object.values(allowedFileTypes).flat();
    return extensions.join(', ');
  }, [allowedFileTypes]);

  // Colores dinámicos según estado del Drag
  const borderColor = isDragReject ? "red.500" : isDragActive ? "blue.500" : "gray.300";
  const bgColor = isDragActive ? "blue.50" : "gray.50";

  return (
    <VStack spacing={4} align="stretch">
      <Heading size="md" mb={2} color="gray.700">
        {title}
      </Heading>

      <Box
        {...getRootProps()}
        p={8}
        border="2px dashed"
        borderColor={borderColor}
        borderRadius="xl"
        bg={bgColor}
        _hover={{ bg: "gray.100", borderColor: "blue.400" }}
        transition="all 0.2s"
        cursor={(isGlobalUploading || localUploading) ? "not-allowed" : "pointer"}
        opacity={(isGlobalUploading || localUploading) ? 0.6 : 1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <input {...getInputProps()} />

        <Icon
          as={MdCloudUpload}
          w={12}
          h={12}
          color={isDragActive ? "blue.500" : "gray.400"}
          mb={3}
        />

        <Text color="gray.600" fontSize="md" fontWeight="medium" textAlign="center">
          {isDragActive
            ? "¡Suelta los archivos aquí!"
            : "Arrastra y suelta tus archivos aquí o haz clic para buscar"}
        </Text>

        <Text color="gray.400" fontSize="xs" textAlign="center" mt={2} px={4}>
          Permitidos: {fileTypesText}
        </Text>
      </Box>

      {/* Lista de Progreso de Archivos */}
      {Object.keys(uploadProgress).length > 0 && (
        <VStack align="stretch" spacing={3} mt={2}>
            <Text fontSize="sm" fontWeight="bold" color="gray.600">Estado de carga:</Text>
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <Box
                    key={fileName}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="white"
                    boxShadow="sm"
                >
                    <HStack justifyContent="space-between" mb={1}>
                        <HStack>
                            <Icon as={FaFileAlt} color="blue.500" />
                            <Text fontSize="sm" noOfLines={1} maxW="200px" title={fileName}>
                                {fileName}
                            </Text>
                        </HStack>
                        <HStack>
                            {progress === 100 ? (
                                <Icon as={MdCheckCircle} color="green.500" />
                            ) : (
                                <Text fontSize="xs" fontWeight="bold">{progress}%</Text>
                            )}
                        </HStack>
                    </HStack>
                    <Progress
                        value={progress}
                        size="xs"
                        colorScheme={progress === 100 ? "green" : "blue"}
                        borderRadius="full"
                        hasStripe={progress < 100}
                        isAnimated={progress < 100}
                    />
                </Box>
            ))}
        </VStack>
      )}
    </VStack>
  );
};

export default MultiFileUpload;